// packages/supabase-client/src/react/pages/RegisterPage.tsx

import { Link } from 'react-router-dom';
import { AuthLayout } from '../components/AuthLayout';
import { SignUpForm } from '../forms/SignUpForm';
import { OAuthProviders } from '../components';
import { useAuthService, useAuthText, useAuthUI, useAuthErrorMessage, useAuthFlow } from '../hooks';
import { AuthFormData } from '../types/form';

export const RegisterPage: React.FC = () => {
  const authService = useAuthService();
  const { text, resolveText } = useAuthText();
  const { branding, layout, routes, redirects } = useAuthUI();
  const { resolveErrorMessage } = useAuthErrorMessage();

  const flow = useAuthFlow();

  const handleSignUp = async (data: Pick<AuthFormData, 'email' | 'password'>) => {
    flow.start();

    const result = await authService.signUpWithEmailPassword(data.email, data.password, {
      emailRedirectTo: redirects.signUpRedirectTo,
    });

    if (result.success && !result.error) {
      flow.succeed();
    } else {
      if (result.errorCode === 'user_already_registered') {
        flow.fail(
          <>
            {resolveText(text.errors?.userAlreadyRegistered, 'auth:errors.userAlreadyRegistered')}{' '}
            <Link to={routes.login} className="font-bold underline hover:text-emerald-700 transition-colors">
              {resolveText(text.errors?.pleaseSignIn, 'auth:errors.pleaseSignIn')}
            </Link>
          </>
        );
      } else {
        flow.fail(resolveErrorMessage(result.error));
      }
    }
  };

  if (flow.isSuccess) {
    return (
      <AuthLayout 
        title={resolveText(text.signUpSuccessTitle, 'auth:signUpSuccessTitle')} 
        subtitle={resolveText(text.signUpSuccessSubtitle, 'auth:signUpSuccessSubtitle')}
        brandingSubtitle={branding.brandingSubtitle ?? resolveText(text.brandingSubtitle, 'auth:branding.subtitle')}
        brandingLogo={branding.logo}
        showBrandPanel={branding.showBrandPanel}
        variant={layout.variant}
        rootClassName={layout.rootClassName}
        brandPanelClassName={layout.brandPanelClassName}
        formPanelClassName={layout.formPanelClassName}
        cardClassName={layout.cardClassName}
        titleClassName={layout.titleClassName}
        subtitleClassName={layout.subtitleClassName}
      >
        <div className="text-center">
          <p className="text-slate-600">{resolveText(text.checkYourEmail, 'auth:checkYourEmail')}</p>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout 
      title={resolveText(text.signUpTitle, 'auth:signUpTitle')} 
      subtitle={resolveText(text.signUpSubtitle, 'auth:signUpSubtitle')}
      brandingSubtitle={branding.brandingSubtitle ?? resolveText(text.brandingSubtitle, 'auth:branding.subtitle')}
      brandingLogo={branding.logo}
      showBrandPanel={branding.showBrandPanel}
      variant={layout.variant}
      rootClassName={layout.rootClassName}
      brandPanelClassName={layout.brandPanelClassName}
      formPanelClassName={layout.formPanelClassName}
      cardClassName={layout.cardClassName}
      titleClassName={layout.titleClassName}
      subtitleClassName={layout.subtitleClassName}
    >
      {/* OAuth at top */}
      <OAuthProviders />
      {/* Divider */}
      <div className="relative my-6">
        <div className="absolute inset-0 flex items-center">
          <span className="w-full border-t border-slate-300" />
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="bg-white px-2 text-slate-500">{resolveText(text.orDivider, 'common:or')}</span>
        </div>
      </div>

      <SignUpForm
        onSubmit={handleSignUp}
        isPending={flow.isPending}
        errorMessage={flow.error}
      />
    </AuthLayout>
  );
};
